/**
 * Firebase Web SDK wrapper for playground mode.
 *
 * This client provides:
 * 1. Firebase initialization with environment-specific config
 * 2. Google Sign-In authentication for dev/staging
 * 3. Email/password authentication for local emulator mode
 * 4. Firestore helpers for direct database operations
 * 5. Emulator connection for local development
 *
 * Auth flow (dev/staging):
 * - Developer signs in via Google Sign-In in the playground toolbar
 * - Session persisted in IndexedDB (browserLocalPersistence) across reloads
 *
 * Auth flow (local emulator):
 * - User signs in via toolbar with email/password against Auth emulator
 * - Session persisted in IndexedDB (browserLocalPersistence)
 */

import { getPlaygroundConfig, type PlaygroundConfig } from '../config/playground'
import { resolveIdTokenOrThrow } from './tokenRefresh'
import { buildLocalPlaygroundEmail, generateRandomPlayerId, normalizeLocalPlaygroundPlayerId } from '../playground/localPlaygroundIdentity'
import { cancelBootReload, expectBootReload, markBootPhase, setBootAuthPath, setBootSlot, timeBootPhase } from '../playground/BootTelemetry'

// Firebase types - we use dynamic imports to avoid bundling Firebase in production
type FirebaseApp = import('firebase/app').FirebaseApp
type Auth = import('firebase/auth').Auth
type User = import('firebase/auth').User
type UserCredential = import('firebase/auth').UserCredential
type Firestore = import('firebase/firestore').Firestore

/**
 * Firebase client interface providing auth and Firestore access.
 */
export interface FirebaseClient {
  app: FirebaseApp
  auth: Auth
  firestore: Firestore
  config: PlaygroundConfig

  // Auth helpers
  isSignedIn(): boolean
  getProfileId(): string | null
  /**
   * Local-only: sign in as a deterministic test user (player1, player2, ...).
   * Uses the Auth emulator and email/password.
   */
  signInWithLocalPlayer(playerId: string): Promise<UserCredential>
  signOut(): Promise<void>
  getIdToken(): Promise<string | null>
  getIdTokenOrThrow(): Promise<string>
  getCurrentUser(): User | null
  onAuthStateChanged(callback: (user: User | null) => void): () => void

  // Firestore helpers
  doc: typeof import('firebase/firestore').doc
  collection: typeof import('firebase/firestore').collection
  getDoc: typeof import('firebase/firestore').getDoc
  getDocs: typeof import('firebase/firestore').getDocs
  setDoc: typeof import('firebase/firestore').setDoc
  updateDoc: typeof import('firebase/firestore').updateDoc
  deleteDoc: typeof import('firebase/firestore').deleteDoc
  addDoc: typeof import('firebase/firestore').addDoc
  writeBatch: typeof import('firebase/firestore').writeBatch
  runTransaction: typeof import('firebase/firestore').runTransaction
  query: typeof import('firebase/firestore').query
  where: typeof import('firebase/firestore').where
  orderBy: typeof import('firebase/firestore').orderBy
  limit: typeof import('firebase/firestore').limit
  onSnapshot: typeof import('firebase/firestore').onSnapshot
  serverTimestamp: typeof import('firebase/firestore').serverTimestamp
  deleteField: typeof import('firebase/firestore').deleteField
}

const APP_NAME = 'rundot-game-sandbox'
let firebaseClientPromise: Promise<FirebaseClient> | null = null
let cachedClient: FirebaseClient | null = null

const LOCAL_TEST_PASSWORD = 'rundot-game-dev-password'

/**
 * Get the Firebase client instance.
 * Initializes Firebase on first call and caches the instance.
 */
export async function getFirebaseClient(): Promise<FirebaseClient> {
  if (cachedClient) {
    return cachedClient
  }

  if (!firebaseClientPromise) {
    firebaseClientPromise = initializeFirebaseClient()
  }

  try {
    cachedClient = await firebaseClientPromise
    return cachedClient
  } catch (error) {
    // Clear the cached promise so subsequent calls can retry instead of
    // permanently returning the same rejected promise.
    firebaseClientPromise = null
    throw error
  }
}

/**
 * Check if Firebase has been initialized.
 */
export function isFirebaseInitialized(): boolean {
  return cachedClient !== null
}

// ─── Token Exchange Helpers (dev/staging only) ─────────────────────────────

/**
 * Exchange a valid ID token for a Firebase custom token via the Cloud Run endpoint.
 * The custom token can be used with signInWithCustomToken() to establish a full
 * Firebase Auth session (required for Firestore security rules, subscriptions, etc.).
 *
 * Uses raw fetch() — no dependency on getFirebaseClient() or getIdTokenOrThrow().
 * This is called DURING Firebase client initialization, before auth is established.
 */
export async function exchangeForCustomToken(
  idToken: string,
  cloudRunBaseUrl: string,
  playerId?: string,
): Promise<string> {
  const url = `${cloudRunBaseUrl}/v1/sandbox-auth/exchange`
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify(playerId ? { playerId } : {}),
  })

  if (!response.ok) {
    const text = await response.text()
    throw new Error(`[RUN] Custom token exchange failed (${response.status}): ${text}`)
  }

  const data = (await response.json()) as Record<string, unknown>
  const customToken = data.customToken
  if (typeof customToken !== 'string' || customToken.length === 0) {
    throw new Error('[RUN] Custom token exchange: response missing customToken')
  }
  return customToken
}

// ─── API Key Exchange (headless/automated testing) ──────────────────────────

/**
 * Dev-server middleware path that performs the headless key exchange Node-side.
 * Must match RUNDOT_HEADLESS_AUTH_PATH in RundotGamePlaygroundPlugin.ts.
 */
const RUNDOT_HEADLESS_AUTH_PATH = '/__rundot-headless-auth'

const PLAYGROUND_KEY_RENEWAL_COMMAND = '`rundot playground grant-access`'

interface ParsedHeadlessError {
  code?: string
  retryAfter?: number
  rawText: string
}

function parseHeadlessAuthErrorPayload(responseText: string): ParsedHeadlessError {
  let currentText = responseText
  let extractedCode: string | undefined
  let extractedRetryAfter: number | undefined

  // Unwrap up to 2 levels of nested JSON envelopes (e.g. Vite proxy -> Cloud Run)
  for (let depth = 0; depth < 2; depth++) {
    try {
      const parsed = JSON.parse(currentText) as Record<string, unknown>
      if (typeof parsed.code === 'string' && !extractedCode) {
        extractedCode = parsed.code
      }
      if (typeof parsed.retryAfter === 'number' && extractedRetryAfter === undefined) {
        extractedRetryAfter = parsed.retryAfter
      }
      if (typeof parsed.error === 'string') {
        currentText = parsed.error
        continue
      }
      if (typeof parsed.message === 'string') {
        currentText = parsed.message
        continue
      }
      break
    } catch {
      break
    }
  }

  // Fallback to substring matching on normalized text
  const normalized = responseText.toLowerCase()
  if (!extractedCode) {
    if (normalized.includes('api key expired') || normalized.includes('playground-key-expired')) {
      extractedCode = 'playground-key-expired'
    } else if (normalized.includes('api key revoked') || normalized.includes('playground-key-revoked')) {
      extractedCode = 'playground-key-revoked'
    } else if (normalized.includes('playground-key-invalid')) {
      extractedCode = 'playground-key-invalid'
    }
  }

  // Fallback to regex for retryAfter if present in string
  if (extractedRetryAfter === undefined) {
    const match = /retryafter["':\s]+(\d+)/i.exec(responseText)
    if (match) {
      extractedRetryAfter = parseInt(match[1], 10)
    }
  }

  return { code: extractedCode, retryAfter: extractedRetryAfter, rawText: responseText }
}

function formatHeadlessAuthError(status: number, responseText: string): string {
  const { code, retryAfter } = parseHeadlessAuthErrorPayload(responseText)

  switch (code) {
    case 'playground-key-expired':
      return `[RUN] Your playground key has expired. Run ${PLAYGROUND_KEY_RENEWAL_COMMAND}, then restart the dev server.`
    case 'playground-key-revoked':
      return `[RUN] Your playground key was revoked. Run ${PLAYGROUND_KEY_RENEWAL_COMMAND}, then restart the dev server.`
    case 'playground-key-invalid':
      return `[RUN] Your playground key is invalid. Run ${PLAYGROUND_KEY_RENEWAL_COMMAND}, then restart the dev server.`
    default:
      if (status === 429 || responseText.includes('Too many failed API key exchange attempts')) {
        const waitStr = retryAfter !== undefined ? `${retryAfter}s` : 'a minute'
        return `[RUN] Headless auth rate limit exceeded. This usually occurs when an invalid or expired playground key is retried repeatedly. If errors persist, run ${PLAYGROUND_KEY_RENEWAL_COMMAND} to check your key, wait ${waitStr}, and restart the dev server.`
      }
      return `[RUN] Headless auth failed (${status}): ${responseText}`
  }
}

/**
 * Fetch a short-lived Firebase custom token from the dev-server's headless-auth
 * middleware. The raw key lives only on the dev server; this browser context
 * never sees it. The `X-Rundot-Playground` header satisfies the middleware's CSRF
 * guard (a cross-origin page cannot set it without a preflight).
 *
 * Uses raw fetch() — no dependency on getFirebaseClient() or getIdTokenOrThrow().
 * This is called DURING Firebase client initialization, before auth is established.
 */
/**
 * True when the current browser origin is loopback (localhost / 127.0.0.1 / ::1).
 * Google OAuth works from a loopback origin, so the LAN anonymous fallback must
 * NOT engage there even when the dev server (bound to 0.0.0.0, which serves both
 * the LAN IP and localhost) advertises it — otherwise a developer loading
 * `http://localhost` directly, or a phone reaching it via `adb reverse`, would be
 * forced anonymous instead of getting the normal Google sign-in.
 */
function isLoopbackBrowserOrigin(): boolean {
  if (typeof window === 'undefined') return true
  const host = window.location.hostname.toLowerCase()
  return (
    host === 'localhost' ||
    host === '::1' ||
    host === '[::1]' ||
    host === '' ||
    host.endsWith('.localhost') ||
    host.startsWith('127.')
  )
}

export async function fetchPreviewCustomToken(
  tokenUrl: string,
  ticket: string | undefined,
): Promise<string> {
  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ticket }),
  })
  const responseText = await response.text()
  if (!response.ok) {
    throw new Error(`[RUN] Preview sign-in failed (${response.status}): ${responseText}`)
  }
  const data = JSON.parse(responseText) as { customToken?: unknown }
  if (typeof data.customToken !== 'string' || data.customToken.length === 0) {
    throw new Error('[RUN] Preview sign-in: response missing customToken')
  }
  return data.customToken
}

export async function fetchHeadlessCustomToken(): Promise<string> {
  const response = await fetch(RUNDOT_HEADLESS_AUTH_PATH, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Rundot-Playground': '1',
    },
  })

  if (!response.ok) {
    const text = await response.text()
    throw new Error(formatHeadlessAuthError(response.status, text))
  }

  const data = (await response.json()) as { customToken?: unknown }
  const customToken = data.customToken
  if (typeof customToken !== 'string' || customToken.length === 0) {
    throw new Error('[RUN] Headless auth: response missing customToken')
  }
  return customToken
}

// ─── Firebase Client Initialization ────────────────────────────────────────

/**
 * Initialize the Firebase client with dynamic imports.
 */
async function initializeFirebaseClient(): Promise<FirebaseClient> {
  const config = getPlaygroundConfig()
  if (!config || !config.firebaseConfig) {
    throw new Error(
      '[RUN] Cannot initialize Firebase: Playground config not available. ' +
      'Make sure the Vite plugin is configured correctly.'
    )
  }

  const isLocalMode = config.target === 'local'

  // Boot telemetry: no-ops outside playground mode (BootTelemetry singleton is
  // only constructed when isPlaygroundEnabled()).
  const initStart = Date.now()

  const [
    firebaseApp,
    firebaseAuth,
    firebaseFirestore,
  ] = await Promise.all([
    import('firebase/app'),
    import('firebase/auth'),
    import('firebase/firestore'),
  ])
  const { initializeApp, getApps } = firebaseApp
  const {
    getAuth,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signInAnonymously,
    signInWithCustomToken,
    browserLocalPersistence,
    inMemoryPersistence,
    setPersistence,
    connectAuthEmulator,
    getRedirectResult,
  } = firebaseAuth
  const {
    getFirestore,
    connectFirestoreEmulator,
    doc, collection, getDoc, getDocs, setDoc, updateDoc, deleteDoc, addDoc, writeBatch,
    runTransaction,
    query, where, orderBy, limit, onSnapshot, serverTimestamp, deleteField,
  } = firebaseFirestore

  const existingApps = getApps()
  const existingApp = existingApps.find(app => app.name === APP_NAME)
  const app = existingApp ?? initializeApp(config.firebaseConfig!, APP_NAME)

  const auth = getAuth(app)

  // Connect to Auth emulator for local mode
  if (isLocalMode && config.authEmulatorHost) {
    try {
      connectAuthEmulator(auth, `http://${config.authEmulatorHost}`, { disableWarnings: true })
    } catch (e) {
      console.warn('[RUN] Auth emulator connection error (may already be connected):', e)
    }
  }

  if (config.sandboxedOrigin) {
    await setPersistence(auth, inMemoryPersistence)
  } else {
    // Primary/shared app uses IndexedDB (browserLocalPersistence) so the Google
    // credential from Tab 1 is accessible to all tabs for token exchange.
    try {
      await setPersistence(auth, browserLocalPersistence)
    } catch (e) {
      console.warn('[RUN] Persistence error:', e)
    }
  }

  // Process any pending Google OAuth redirect result. Only needed for
  // dev/staging/playground where signInWithRedirect may be used as a
  // fallback for embedded browsers. Local mode never uses Google OAuth.
  if (!isLocalMode) {
    try {
      await getRedirectResult(auth)
    } catch (redirectError) {
      console.warn('[RUN] Google redirect result error (non-fatal):', redirectError)
    }
  }

  markBootPhase('firebase-init', Date.now() - initStart)

  let sessionRestoreTimedOut = false
  const initialUser = await timeBootPhase('firebase-session-restore', () =>
    new Promise<User | null>((resolve) => {
      const unsubscribe = auth.onAuthStateChanged((user) => {
        unsubscribe()
        resolve(user)
      })
      // Timeout fallback in case onAuthStateChanged doesn't fire.
      // 10s is generous — onAuthStateChanged is guaranteed to fire, but slow
      // networks or large IndexedDB stores can delay session restoration.
      setTimeout(() => {
        sessionRestoreTimedOut = true
        unsubscribe()
        resolve(null)
      }, 10000)
    }),
  )
  if (sessionRestoreTimedOut) {
    markBootPhase('firebase-session-restore:timeout')
  }

  /** Check if a user has a Google provider (not just "not anonymous"). */
  const hasGoogleProvider = (user: User): boolean =>
    user.providerData?.some(p => p.providerId === 'google.com') === true

  // ─── Headless base sign-in (headless/automated testing) ─────────────────
  // Sign the BASE identity into the SHARED auth using a short-lived custom token
  // from the localhost-only /__rundot-headless-auth middleware. The raw key stays
  // on the dev server. Always (re)establishes the base — a stale non-pk_ session
  // left in IndexedDB (e.g. a prior interactive Google run in the same browser)
  // must be overwritten, never silently adopted. config.hasHeadlessAuth implies
  // the playground target, so this is only ever called from the headless branch
  // in the playground tab-coordinator section below.
  const establishHeadlessBase = async (): Promise<void> => {
    setBootAuthPath('headless')
    const customToken = await timeBootPhase('firebase-headless-token', () =>
      fetchHeadlessCustomToken(),
    )
    await timeBootPhase('firebase-headless-signin', () =>
      signInWithCustomToken(auth, customToken),
    )
  }

  const establishPreviewSession = async (
    tokenUrl: string,
    ticket: string | undefined,
  ): Promise<void> => {
    setBootAuthPath('headless')
    const customToken = await timeBootPhase('firebase-headless-token', () =>
      fetchPreviewCustomToken(tokenUrl, ticket),
    )
    await timeBootPhase('firebase-headless-signin', () =>
      signInWithCustomToken(auth, customToken),
    )
  }

  // ─── Local Emulator Bootstrap ────────────────────────────────────────
  // Ensure the signed-in user matches the desired player from localStorage.
  // On first visit: no user in IndexedDB → sign in as a new random player.
  // On reload after player switch: IndexedDB may still have the OLD user
  // (persistence race or stale session), so we always compare and re-sign-in
  // if there's a mismatch.
  //
  // Exception: after an explicit sign-out, skip auto-sign-in so the user
  // sees the player selection screen and can choose a different player.
  if (isLocalMode) {
    let skipAutoSignIn = false
    try {
      if (sessionStorage.getItem('rundot-game-sandbox-signed-out')) {
        sessionStorage.removeItem('rundot-game-sandbox-signed-out')
        skipAutoSignIn = true
      }
    } catch { /* storage unavailable */ }

    if (!skipAutoSignIn) {
      let localPlayerId: string
      try {
        const stored = localStorage.getItem('rundot-game-sandbox-selected-player')
        const candidate = stored?.trim().toLowerCase()
        if (candidate && (/^player\d+$/.test(candidate) || /^player_[0-9a-f]{4}$/.test(candidate))) {
          localPlayerId = candidate
        } else {
          // First visit in this browser — generate a unique player identity
          localPlayerId = generateRandomPlayerId()
          localStorage.setItem('rundot-game-sandbox-selected-player', localPlayerId)
        }
      } catch {
        // localStorage unavailable — generate ephemeral identity
        localPlayerId = generateRandomPlayerId()
      }

      const desiredEmail = buildLocalPlaygroundEmail(normalizeLocalPlaygroundPlayerId(localPlayerId))

      // Compare restored user (from IndexedDB) with the desired player.
      // If they match, skip sign-in. If they differ (e.g., player switched
      // via toolbar but IndexedDB still has old session), re-sign-in.
      if (!initialUser || initialUser.email !== desiredEmail) {
        try {
          await signInWithEmailAndPassword(auth, desiredEmail, LOCAL_TEST_PASSWORD)
        } catch (error: unknown) {
          const authError = error as { code?: string }
          if (authError.code === 'auth/user-not-found' || authError.code === 'auth/invalid-credential') {
            await createUserWithEmailAndPassword(auth, desiredEmail, LOCAL_TEST_PASSWORD)
          } else {
            console.warn('[RUN] Local auto-sign-in failed (emulator may not be ready):', error)
          }
        }
      }
    }
  }

  // For playground: use tab coordinator for multi-tab slot assignment.
  //
  // KEY DESIGN: The shared app (`rundot-game-sandbox`) uses browserLocalPersistence
  // so all tabs can read the Google credential for token exchange. But secondary tabs
  // must NOT call signInWithCustomToken on the shared auth — that would overwrite the
  // Google session for all tabs. Instead, secondary tabs create their OWN Firebase app
  // with browserSessionPersistence (per-tab isolation) and sign in on that.
  //
  // The variables `auth`, `app` declared above point to the shared app.
  // For secondary tabs, `activeAuth` / `activeApp` will point to the tab-local app.
  let activeAuth = auth
  let activeApp = app

  if (config.previewTokenUrl) {
    setBootSlot(0)
    await establishPreviewSession(config.previewTokenUrl, config.previewTicket)
  } else if (config.target === 'playground') {
    const tabCoord = await import('../playground/tabCoordinator')
    const { parseForcedPlayerId } = await import('../playground/forcedPlayerIdentity')
    const { browserSessionPersistence } = firebaseAuth

    // URL-forced identity (?rundotPlayer=<id>): pins THIS browser instance to a
    // distinct synthetic player regardless of coordinator state. The primary
    // use is cross-browser / cross-context multiplayer testing, where the
    // coordinator's localStorage registry is not shared and every instance
    // would otherwise claim slot 0 (the shared base identity) and collide in
    // rooms with DUPLICATE_SESSION. Absent/invalid → null → unchanged flow.
    const forcedPlayerId = parseForcedPlayerId(
      typeof window !== 'undefined' ? window.location.search : undefined,
    )

    /**
     * Apply a forced identity over a coordinator assignment: warn on a
     * same-browser conflict, persist the forced id into this tab's registry
     * entry (so the toolbar pool and later conflict checks see it), and
     * return the id to activate.
     */
    const applyForced = (assigned: string | null, tabId: string): string | null => {
      if (forcedPlayerId === null) return assigned
      // 'anonymous' is exempt from the conflict warning: signInAsTabLocal
      // gives each tab a fresh random anonymous uid, so same-value tabs do
      // not collide on one identity.
      if (forcedPlayerId !== 'anonymous' && tabCoord.hasLiveTabWithPlayerId(forcedPlayerId, tabId)) {
        console.warn(
          `[RUN:playground] ?rundotPlayer=${forcedPlayerId} is already in use by another tab ` +
          'in this browser — both will share one identity and the room server will reject the ' +
          'second with DUPLICATE_SESSION. Canonical fixes: give each isolated browser/context ' +
          'a distinct ?rundotPlayer= value, or run players as tabs in a single browser window ' +
          '(automatic assignment).',
        )
      }
      tabCoord.setPlayerId(tabId, forcedPlayerId)
      return forcedPlayerId
    }

    // ── Defensive cleanup: wipe stale coordinator state on first load ──
    // If all tabs were closed and the browser reopened, stale entries can
    // leave `googleSignedIn: true` or stale tab entries that cause new tabs
    // to get slot -1 (deadlock: sign-in needs Firebase client, which blocks
    // on waitForGoogleSignIn).  We detect this by checking whether any
    // non-stale tabs exist.  If not, clear the entire registry so this tab
    // gets slot 0 as the primary.
    {
      const reg = JSON.parse(localStorage.getItem('rundot-playground-tabs') || '{}')
      const tabs = reg?.tabs ? Object.values(reg.tabs) as Array<{ timestamp: number }> : []
      const now = Date.now()
      const anyAlive = tabs.some(t => now - t.timestamp < 30_000)
      if (!anyAlive && (reg?.googleSignedIn || tabs.length > 0)) {
        tabCoord.clearRegistry()
      }
    }

    /**
     * Wait for the coordinator's `googleSignedIn` flag, then wait for the
     * shared Firebase auth to actually have a Google user in memory.
     * This handles the race where the localStorage event arrives before
     * Firebase's cross-tab BroadcastChannel sync.
     */
    const waitForGoogleUser = (): Promise<User> => new Promise<User>((resolve, reject) => {
      // If we already have one, resolve immediately
      const current = auth.currentUser
      if (current && hasGoogleProvider(current)) { resolve(current); return }

      // Listen on the shared auth for a Google user to appear
      const unsub = auth.onAuthStateChanged((user) => {
        if (user && hasGoogleProvider(user)) {
          unsub()
          clearTimeout(timer)
          resolve(user)
        }
      })
      const timer = setTimeout(() => {
        unsub()
        reject(new Error('[RUN] Timed out waiting for Google user on shared auth'))
      }, 30_000)
    })

    /**
     * Wait for Google sign-in to be available.
     * Resolves when a Google user appears — either via coordinator flag (cross-tab)
     * or via direct sign-in on the shared auth (same-tab).
     */
    const waitForGoogleSignIn = async (): Promise<User> => {
      // Race: coordinator flag from another tab  OR  direct Google user on shared auth
      return new Promise<User>((resolve, reject) => {
        let settled = false
        const settle = (user: User) => {
          if (settled) return
          settled = true
          unsubCoord()
          unsubAuth()
          clearTimeout(timer)
          // Ensure coordinator flag is set (covers same-tab sign-in case)
          tabCoord.setGoogleSignedIn(true)
          resolve(user)
        }

        // Path 1: coordinator flag (cross-tab sign-in)
        const unsubCoord = tabCoord.isGoogleSignedIn()
          ? (() => { /* already signed in, waitForGoogleUser below will resolve */ return () => {} })()
          : tabCoord.onGoogleSignedIn(() => {
              // Flag set by another tab — wait for Firebase to sync the user
              waitForGoogleUser().then(settle).catch(() => {})
            })

        // Path 2: direct Google user on shared auth (same-tab sign-in)
        const unsubAuth = auth.onAuthStateChanged((user) => {
          if (user && hasGoogleProvider(user)) settle(user)
        })

        // If coordinator already flagged, also kick off waitForGoogleUser
        if (tabCoord.isGoogleSignedIn()) {
          waitForGoogleUser().then(settle).catch(() => {})
        }

        const timer = setTimeout(() => {
          if (!settled) {
            settled = true
            unsubCoord()
            unsubAuth()
            reject(new Error('[RUN] Timed out waiting for Google sign-in'))
          }
        }, 60_000)
      })
    }

    /**
     * Create a tab-local Firebase app + auth and sign in with the given playerId.
     * - playerId is null → exchange with no playerId (gets real/default profile)
     * - playerId is 'playerN' → exchange with that playerId (synthetic)
     * - playerId is 'anonymous' → signInAnonymously
     */
    const signInAsTabLocal = async (playerId: string | null, googleUser: User): Promise<{ tabApp: FirebaseApp; tabAuth: Auth }> => {
      // Create a separate Firebase app for this tab (sessionStorage = per-tab isolation)
      const tabAppName = APP_NAME + '-tab'
      const existingTabApp = getApps().find(a => a.name === tabAppName)
      const tabApp = existingTabApp ?? initializeApp(config.firebaseConfig!, tabAppName)
      const tabAuth = getAuth(tabApp)
      try {
        await setPersistence(tabAuth, browserSessionPersistence)
      } catch {
        // ignore
      }

      if (playerId === 'anonymous') {
        await timeBootPhase('firebase-tab-local-signin', () => signInAnonymously(tabAuth))
      } else {
        const cloudRunBase = config.cloudRunUrl || '/__rundotcloudrun'
        const customToken = await timeBootPhase('firebase-custom-token-exchange', async () => {
          const idToken = await googleUser.getIdToken(true)
          return exchangeForCustomToken(idToken, cloudRunBase, playerId ?? undefined)
        })

        await timeBootPhase('firebase-tab-local-signin', () =>
          signInWithCustomToken(tabAuth, customToken),
        )
      }
      return { tabApp, tabAuth }
    }

    /** Activate a tab-local app if playerId is non-default. */
    const activateTabLocal = async (pid: string | null, googleUser: User): Promise<void> => {
      if (pid !== null) {
        const { tabApp, tabAuth } = await signInAsTabLocal(pid, googleUser)
        activeApp = tabApp
        activeAuth = tabAuth
      }
    }

    if (config.hasHeadlessAuth) {
      // Headless multi-tab: the base credential is deterministic and available to
      // every tab on demand (fetchHeadlessCustomToken always yields the same base
      // uid), so there is no wait-for-slot-0 dance. Mark the shared credential
      // ready up-front so concurrent tabs are routed to distinct slots
      // (player1/2/3) instead of the slot -1 reclaim path. Each tab establishes
      // the base itself, then activates its assigned synthetic player.
      tabCoord.setGoogleSignedIn(true)
      const assigned = tabCoord.getOrAssignSlot()
      const playerId = applyForced(assigned.playerId, assigned.tabId)
      setBootSlot(assigned.slot)
      if (typeof window !== 'undefined') {
        window.__RUNDOT_GAME_PLAYGROUND_TAB_INFO__ = { slot: assigned.slot, tabId: assigned.tabId, playerId }
      }
      try {
        await establishHeadlessBase()
      } catch (error) {
        console.error('[RUN] Headless base sign-in failed:', error)
        throw error
      }
      const baseUser = auth.currentUser
      if (baseUser) {
        try {
          // slot 0 (playerId null) stays on the shared base user; slots 1+ get a
          // tab-local app signed in as the synthetic ${baseUid}_sandbox_playerN.
          await activateTabLocal(playerId, baseUser)
        } catch (error) {
          console.warn(`[RUN] Playground headless slot ${assigned.slot}: tab-local sign-in as ${playerId} failed:`, error)
        }
      }
    } else {
    setBootAuthPath('interactive')
    let { slot, tabId, playerId } = tabCoord.getOrAssignSlot()
    playerId = applyForced(playerId, tabId)
    setBootSlot(slot)

    // Expose tab info globally for toolbar
    if (typeof window !== 'undefined') {
      window.__RUNDOT_GAME_PLAYGROUND_TAB_INFO__ = { slot, tabId, playerId }
    }

    // ── Slot -1 reclaim: always promote to slot 0 ──
    // Any tab must be able to sign in. Slot -1 blocks on waitForGoogleSignIn()
    // which requires the toolbar to show sign-in controls — but the toolbar
    // only shows them for slot 0. So we always clear the registry and reclaim
    // slot 0. This is safe: the shared Firebase app uses browserLocalPersistence,
    // so whichever tab signs in first will propagate the Google session to all
    // other tabs via the coordinator flag + cross-tab BroadcastChannel.
    if (slot === -1) {
      tabCoord.clearRegistry()
      const reassigned = tabCoord.getOrAssignSlot()
      slot = reassigned.slot
      tabId = reassigned.tabId
      playerId = applyForced(reassigned.playerId, tabId)
      setBootSlot(slot)
      if (typeof window !== 'undefined') {
        window.__RUNDOT_GAME_PLAYGROUND_TAB_INFO__ = { slot, tabId, playerId }
      }
    }

    // ── Handle based on final slot value ──
    if (slot === 0) {
      // Primary tab: use cached Google session or wait for sign-in via toolbar
      if (initialUser && hasGoogleProvider(initialUser)) {
        tabCoord.setGoogleSignedIn(true)

        // If slot 0 was switched to a non-default playerId, use a tab-local app
        try {
          await activateTabLocal(playerId, initialUser)
        } catch (error) {
          console.warn(`[RUN] Playground slot 0: tab-local sign-in as ${playerId} failed:`, error)
        }
      } else if (config.allowLanAnonymousAuth && !isLoopbackBrowserOrigin()) {
        // LAN/mobile testing: Google OAuth can't run from a non-localhost origin
        // (Firebase authorized-domains), so the primary tab would otherwise hang
        // on "signing in" forever. Sign in anonymously so the game loads.
        //
        // Two gates, both required: the dev server sets the flag ONLY on a
        // non-loopback bind (never on a production build — so the hosted
        // playground still requires Google), and we additionally check THIS
        // browser origin. A single dev server bound to 0.0.0.0 serves both the
        // LAN IP and localhost; loading via localhost (incl. `adb reverse`) can
        // do Google, so only the genuinely non-loopback origin falls back here.
        //
        // Reuse a cached anonymous session across reloads instead of minting a
        // new user each time.
        tabCoord.setGoogleSignedIn(false)
        if (!auth.currentUser) {
          // Fail loud, don't swallow: if anonymous sign-in fails (e.g. the
          // Firebase project has the Anonymous provider disabled) there is no
          // way to obtain a user. Let it throw so getFirebaseClient() rejects and
          // the auth state machine surfaces an error (which resolves
          // waitForProfile) instead of hanging on the loader forever — the exact
          // failure this path is meant to remove.
          await signInAnonymously(auth)
        }
      } else {
        // No Google user — clear any stale auth state + coordinator flag
        tabCoord.setGoogleSignedIn(false)
        if (initialUser) {
          await auth.signOut()
        }
      }
    } else if (slot > 0) {
      // Secondary tab: wait for Google user, then sign in on a tab-local app with assigned playerId
      try {
        const googleUser = await waitForGoogleSignIn()
        await activateTabLocal(playerId, googleUser)
      } catch (error) {
        console.warn(`[RUN] Playground slot ${slot}: sign-in failed:`, error)
      }
    }
    }

  }

  const firestore = getFirestore(activeApp)

  // Connect to Firestore emulator for local development
  if (isLocalMode && config.firestoreEmulatorHost) {
    try {
      const [host, port] = config.firestoreEmulatorHost.split(':')
      connectFirestoreEmulator(firestore, host, parseInt(port, 10))
    } catch (e) {
      console.warn('[RUN] Firestore emulator already connected or error:', e)
    }
  }

  const client: FirebaseClient = {
    app: activeApp,
    auth: activeAuth,
    firestore,
    config,

    // Auth helpers
    isSignedIn(): boolean {
      return activeAuth.currentUser !== null
    },

    getProfileId(): string | null {
      return activeAuth.currentUser?.uid ?? null
    },

    async signInWithLocalPlayer(playerId: string): Promise<UserCredential> {
      if (!isLocalMode) {
        throw new Error('[RUN] signInWithLocalPlayer is only available in local (emulator) mode.')
      }

      const normalizedPlayerId = normalizeLocalPlaygroundPlayerId(playerId)
      const email = buildLocalPlaygroundEmail(normalizedPlayerId)

      try {
        const result = await signInWithEmailAndPassword(activeAuth, email, LOCAL_TEST_PASSWORD)
        return result
      } catch (error: unknown) {
        const authError = error as { code?: string }
        if (authError.code === 'auth/user-not-found' || authError.code === 'auth/invalid-credential') {
          const result = await createUserWithEmailAndPassword(activeAuth, email, LOCAL_TEST_PASSWORD)
          return result
        }
        throw error
      }
    },

    async signOut(): Promise<void> {
      // For playground non-default profiles: sign out the shared Google session
      // FIRST, before the tab-local signOut. activeAuth.signOut() triggers
      // onAuthStateChanged → handleSignOut → window.location.reload(), which
      // would interrupt any subsequent async work. By signing out the shared
      // auth first, we ensure the Google session is cleared from IndexedDB
      // before the reload, preventing a stale session on re-init.
      if (config.target === 'playground' && activeAuth !== auth) {
        await auth.signOut()
      }
      await activeAuth.signOut()
    },

    async getIdToken(): Promise<string | null> {
      const user = activeAuth.currentUser
      if (!user) {
        return null
      }
      return await user.getIdToken()
    },

    async getIdTokenOrThrow(): Promise<string> {
      return resolveIdTokenOrThrow({
        currentUser: activeAuth.currentUser,
        isLocal: getPlaygroundConfig()?.target === 'local',
        // Local-emulator recovery only. NEVER signOut() first in the emulator:
        // it revokes ALL outstanding tokens and cascades auth/id-token-revoked.
        // Re-authenticate to mint a fresh session; force-refresh the fresh user.
        reAuthLocal: async (email: string): Promise<string | null> => {
          await signInWithEmailAndPassword(activeAuth, email, LOCAL_TEST_PASSWORD)
          const freshUser = activeAuth.currentUser
          return freshUser ? await freshUser.getIdToken(true) : null
        },
        signOutLocal: () => activeAuth.signOut(),
        warn: (message: string, err: unknown): void => {
          console.warn(message, err)
        },
      })
    },

    getCurrentUser(): User | null {
      return activeAuth.currentUser
    },

    onAuthStateChanged(callback: (user: User | null) => void): () => void {
      return activeAuth.onAuthStateChanged(callback)
    },

    // Firestore helpers
    doc,
    collection,
    getDoc,
    getDocs,
    setDoc,
    updateDoc,
    deleteDoc,
    addDoc,
    writeBatch,
    runTransaction,
    query,
    where,
    orderBy,
    limit,
    onSnapshot,
    serverTimestamp,
    deleteField,
  }

  return client
}

const POPUP_TIMEOUT_MS = 120_000

/**
 * Sign in with Google.
 * Tries signInWithPopup first (better UX — stays on page). If the browser
 * blocks the popup or the popup hangs (broken postMessage channel in some
 * embedded browsers), falls through to signInWithRedirect which navigates
 * the page to Google OAuth and back.
 */
export async function signInWithGoogle(): Promise<void> {
  await timeBootPhase('firebase-google-signin', async () => {
    const client = await getFirebaseClient()
    const { GoogleAuthProvider, signInWithPopup, signInWithRedirect } = await import('firebase/auth')
    const provider = new GoogleAuthProvider()

    try {
      const popupPromise = signInWithPopup(client.auth, provider)
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('popup-timeout')), POPUP_TIMEOUT_MS)
      })
      await Promise.race([popupPromise, timeoutPromise])
    } catch (error: unknown) {
      const code = (error as { code?: string }).code
      const message = (error as { message?: string }).message
      if (code === 'auth/popup-blocked' || message === 'popup-timeout') {
        // The redirect navigates the tab away and back — sessionStorage
        // survives, so flag the boot buffer to merge (not abandon) it, same
        // as the deliberate game-create reload. Pin the navigation point
        // explicitly: the redirect does not reliably halt the JS turn, so
        // timeBootPhase may still settle and record the real sign-in duration.
        markBootPhase('firebase-google-signin:redirect')
        expectBootReload()
        try {
          await signInWithRedirect(client.auth, provider)
        } catch (redirectError) {
          // Redirect setup failed and the tab never navigated — cancel the
          // flag so a later manual refresh is abandoned, not merged.
          cancelBootReload()
          throw redirectError
        }
      } else {
        throw error
      }
    }
  })
}

/**
 * Subscribe to a Firestore document and receive real-time updates.
 *
 * @param path - Firestore document path (e.g., "profiles/abc123/h5AppStorage/myAppId")
 * @param onChange - Callback invoked when the document changes
 * @returns Unsubscribe function to stop listening
 */
export async function observeFirestoreDocument<T>(
  path: string,
  onChange: (data: T | null) => void,
): Promise<() => void> {
  const client = await getFirebaseClient()
  const segments = path.split('/').filter(segment => segment.length > 0)

  if (segments.length === 0) {
    throw new Error(`[RUN] Invalid Firestore path "${path}".`)
  }

  const ref = client.doc(client.firestore, segments.join('/'))
  const unsubscribe = client.onSnapshot(
    ref,
    (snapshot) => {
      onChange(snapshot.exists() ? (snapshot.data() as T) : null)
    },
    (error) => {
      console.error('[RUN] Firestore subscription error:', error)
    },
  )

  return unsubscribe
}

/**
 * Filter condition for Firestore collection queries.
 */
export interface FirestoreFilter {
  field: string
  op: '<' | '<=' | '==' | '!=' | '>=' | '>' | 'array-contains' | 'in' | 'not-in' | 'array-contains-any'
  value: unknown
}

/**
 * Order specification for Firestore collection queries.
 */
export interface FirestoreOrder {
  field: string
  direction: 'asc' | 'desc'
}

/**
 * Options for observeFirestoreCollection.
 */
export interface FirestoreCollectionOptions {
  filters?: FirestoreFilter[]
  orderBy?: FirestoreOrder[]
  limitCount?: number
}

/**
 * Subscribe to a Firestore collection query and receive real-time updates.
 *
 * @param collectionPath - Firestore collection path (e.g., "h5_rooms/roomId/messages")
 * @param options - Query filters and ordering
 * @param onChange - Callback invoked when query results change
 * @returns Unsubscribe function to stop listening
 */
export async function observeFirestoreCollection<T>(
  collectionPath: string,
  options: FirestoreCollectionOptions,
  onChange: (docs: Array<T & { id: string }>) => void,
): Promise<() => void> {
  const client = await getFirebaseClient()

  const collectionRef = client.collection(client.firestore, collectionPath)

  // Build query constraints
  const constraints: Array<
    ReturnType<typeof client.where> |
    ReturnType<typeof client.orderBy> |
    ReturnType<typeof client.limit>
  > = []

  if (options.filters) {
    for (const filter of options.filters) {
      constraints.push(client.where(filter.field, filter.op, filter.value))
    }
  }

  if (options.orderBy) {
    for (const order of options.orderBy) {
      constraints.push(client.orderBy(order.field, order.direction))
    }
  }

  if (options.limitCount) {
    constraints.push(client.limit(options.limitCount))
  }

  const q = client.query(collectionRef, ...constraints)

  const unsubscribe = client.onSnapshot(
    q,
    (snapshot) => {
      const docs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Array<T & { id: string }>
      onChange(docs)
    },
    (error) => {
      console.error('[RUN] Firestore collection subscription error:', error)
    },
  )

  return unsubscribe
}
